// ============================================
// Admin API Client — Axios instance
// All admin API calls go through this
// ============================================
import axios from 'axios';

const adminApi = axios.create({
  baseURL: '/api/admin',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach JWT token + session ID
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach user's selected session (from sidebar session switcher)
    try {
      const sessionStr = localStorage.getItem('auth_session');
      if (sessionStr) {
        const sessionData = JSON.parse(sessionStr);
        const sessionId = sessionData?.currentSession?.id;
        if (sessionId) {
          config.headers['X-Session-Id'] = String(sessionId);
        }
      }
    } catch { /* ignore parse errors */ }
    // Campus picked in the header switcher (OEL-186). Absent = "all I may see".
    try {
      const campusId = localStorage.getItem('auth_campus');
      if (campusId && /^\d+$/.test(campusId)) config.headers['X-Campus-Id'] = campusId;
    } catch { /* ignore */ }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401 (expired token)
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Absolute URL for an admin endpoint opened by the BROWSER rather than by axios —
 * window.open, an <a href>, an <img src>. Those requests never run through the
 * interceptor above, so they carry no Authorization header and the API answers
 * 401 "No token provided". authMiddleware accepts the JWT as a `token` query
 * parameter for exactly this case; this puts it there.
 *
 * Use it for every download / export / print URL. Do not hand-build these. (OEL-77)
 */
export function adminFileUrl(path: string): string {
  const base = adminApi.defaults.baseURL || '/api/admin';
  const clean = path.startsWith('/') ? path : `/${path}`;
  const token = localStorage.getItem('auth_token') || '';
  if (!token) return `${base}${clean}`;
  return `${base}${clean}${clean.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}

/**
 * Force-download a protected admin file with the Authorization header, NOT a
 * URL-embedded token (OEL-XX). window.open(adminFileUrl(...)) works when the
 * `?token=` query param survives all the way to authMiddleware, but there are
 * three real failure modes it hits in the wild:
 *
 *   1. The deployed build is behind main and predates OEL-77, so the URL never
 *      gets the token appended — the server answers 401 "No token provided" and
 *      the user sees raw JSON in a new tab (this is the reported bug).
 *   2. A CDN / reverse-proxy rule strips query strings on certain paths,
 *      producing the same 401.
 *   3. The JWT ends up in browser history, referer headers, and access logs —
 *      an auditable privacy issue for admin sessions.
 *
 * This helper fetches the file as a blob through the axios instance (so the
 * request-interceptor attaches the Authorization header, and the 401 interceptor
 * still routes expired tokens to /login instead of a broken new tab), then
 * synthesises a temporary <a download> click to save it. The filename comes from
 * the server's Content-Disposition when present, else the fallback argument.
 * Fails soft: any error is reported through the optional onError callback so the
 * caller can surface a toast rather than crashing.
 */
export async function downloadAdminFile(
  path: string,
  fallbackName = 'download',
  onError?: (msg: string) => void,
): Promise<void> {
  try {
    const resp = await adminApi.get(path, { responseType: 'blob' });
    // Extract filename from Content-Disposition if the server provided one.
    let name = fallbackName;
    const cd = resp.headers?.['content-disposition'] || resp.headers?.['Content-Disposition'];
    if (cd) {
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(String(cd));
      if (m && m[1]) name = decodeURIComponent(m[1]);
    }
    const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke on the next tick so the browser has a chance to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e: any) {
    // 401 is already handled by the response interceptor (logs out → /login),
    // so anything reaching here is a genuine failure worth surfacing.
    if (e?.response?.status === 401) return;
    const msg = e?.response?.status === 404
      ? 'File not found on the server.'
      : (e?.message || 'Download failed. Please try again.');
    if (onError) onError(msg); else console.error('[downloadAdminFile]', msg);
  }
}

export default adminApi;
